import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAzureResourcesService } from "./finops-azure-resources.service";
import {
    LoadFinOpsAzureResourcesHistoricalData,
    SetFinOpsAzureResource,
    ClearFinOpsAzureResource,
    LoadFinOpsAzureResourcesChartsData,
    LoadDataTableAllSubscriptionsFinOpsAzureResources,
    LoadFinOpsAzureResourcesAllSubscriptionsChartsFilteredData
} from "./finops-azure-resources.actions";
import { finalize, forkJoin, Observable, tap } from "rxjs";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { FinOpsAzureResourcesDayMeasures, FinOpsAzureResourcesStateModel } from "./finops-azure-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { AzureResourceTypeLabelPipe } from "src/app/shared/pipe/azure-resource-type-label.pipe";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { DataForDatatableCompute, FinOpsAzureResourcesHelper } from "./finops-azure-resources.helper";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { AzureConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AZURE_RESOURCES_TOKEN: StateToken<FinOpsAzureResourcesStateModel> = new StateToken('finOpsAzureResources');

const FinOpsAzureResourcesStateModelDefaults: FinOpsAzureResourcesStateModel = {
    historicalDataBySubscription: {},
    datatable: [],
    chartsDataBySubscription: {},
    chartsDataDisplayed: {
        filteredHistoricalChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        },
        filteredCompareProjectsChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        },
        filteredCompareTypesChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        },
    },
    filteredCounters: {},
    detailsData: {
        subscription: '',
        date: '',
        resource: {
            subscription: '',
            type: '',
            name: '',
            location: '',
            tags: Object,
            state: 'compliant',
            projects: [],
            issues: [],
            month_costs: {}
        }
    }
}

@State<FinOpsAzureResourcesStateModel>({
    name: FINOPS_AZURE_RESOURCES_TOKEN,
    defaults: FinOpsAzureResourcesStateModelDefaults
})

@Injectable()
export class FinOpsAzureResourcesState {

    private readonly KEY = FINOPS_AZURE_RESOURCES_TOKEN.getName()

    constructor(
        private finOpsAzureResourcesService: FinOpsAzureResourcesService,
        private store: Store,
        private azureResourceTypeLabelPipe: AzureResourceTypeLabelPipe,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getHistoricalDataBySubscriptionByGranularityByDate(state: FinOpsAzureResourcesStateModel) {
        return (subscription: string, granularity: string, date: string) => state.historicalDataBySubscription[subscription][granularity][date]
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getDatatable(state: FinOpsAzureResourcesStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFilteredCounters(state: FinOpsAzureResourcesStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getChartsDataBySubscriptionAndDate(state: FinOpsAzureResourcesStateModel) {
        return (subscription: string, date:string) => state.chartsDataBySubscription[subscription][date]
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAzureResourcesStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFilteredCompareProjectsChartData(state: FinOpsAzureResourcesStateModel) {
        return state.chartsDataDisplayed.filteredCompareProjectsChartData
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFilteredCompareTypesChartData(state: FinOpsAzureResourcesStateModel) {
        return state.chartsDataDisplayed.filteredCompareTypesChartData
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFinOpsDetailsDate(state: FinOpsAzureResourcesStateModel) {
        return state.detailsData.date
    }

    @Selector([FINOPS_AZURE_RESOURCES_TOKEN])
    static getFinOpsDetailsResource(state: FinOpsAzureResourcesStateModel) {
        return state.detailsData.resource
    }

    @Action(LoadFinOpsAzureResourcesHistoricalData)
    loadFinOpsAzureResourcesHistoricalData(ctx: StateContext<FinOpsAzureResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        const historicalDates: string[] = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        let wrapRequestList = []
        let subscriptions: string[] = []
        azureConfig.subscriptions.forEach(subscription => {
            subscriptions.push(subscription.subscription_id)
        });

        for(const subscription of subscriptions) {
            for (const historalDate of historicalDates) {
                const FINOPS_RESOURCE_DATE_KEY = this.KEY
                + '-subscription-'
                + subscription
                + "-granularity-"
                + finopsConfig.granularity
                + "-date-"
                + historalDate

                if (!this.metaService.isStale(FINOPS_RESOURCE_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_RESOURCE_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }

                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_RESOURCE_DATE_KEY,
                        this.finOpsAzureResourcesService.loadFinOpsAzureResourcesHistoricalData(historalDate, finopsConfig.granularity, subscription),
                        resources => {
                            ctx.setState(state => {
                                const h = {...state.historicalDataBySubscription};
                                h[subscription] = { ...(h[subscription] || {} )};
                                h[subscription][finopsConfig.granularity] = {
                                    ...(h[subscription][finopsConfig.granularity] || {}),
                                    [historalDate]: resources.measures
                                };
                                return {
                                    ...state,
                                    historicalDataBySubscription: h
                                }
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(new LoadDataTableAllSubscriptionsFinOpsAzureResources())
            })
        )
    }

    @Action(LoadDataTableAllSubscriptionsFinOpsAzureResources)
    loadDataTableAllSubscriptionsFinOpsAzureResources(ctx: StateContext<FinOpsAzureResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        const granularity = finopsConfig.granularity as DateGranularityEnum
        const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_RESOURCES]]
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)

        // If cannot find selected date in historical dates, select the last one and load data for it
        if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
            ctx.dispatch([
                new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                new LoadDataTableAllSubscriptionsFinOpsAzureResources()
            ])
            return
        }

        if (isDevMode()) {
            console.warn("Loading all subscriptions data for granularity " + granularity + " and date " + selectedDateTile)
        }

        let total: Record<string, DataForDatatableCompute> = {}
        let grandTotal: DataForDatatableCompute = {
            filteredCounters: {},
            measuresDatatable: {},
            previousTotalCounters: {}            
        }

        for(const subscription of azureConfig.subscriptions) {
            const historicalDataByGranularity = ctx.getState().historicalDataBySubscription[subscription.subscription_id][granularity]
            const dataForDatatable = FinOpsAzureResourcesHelper.computeDataTable(historicalDatesSelected, historicalDataByGranularity, filters)
            total = {
                ...total,
                [subscription.subscription_id]: dataForDatatable
            }
        }

        grandTotal = FinOpsAzureResourcesHelper.computeDatatableGrandTotal(total)

        ctx.patchState(
            {
                datatable: grandTotal.measuresDatatable[selectedDateTile],
                filteredCounters: grandTotal.filteredCounters
            }
        )
    }

    @Action(ClearFinOpsAzureResource)
    clearFinOpsAzureResource(ctx: StateContext<FinOpsAzureResourcesStateModel>) {
        return ctx.patchState({
            detailsData: FinOpsAzureResourcesStateModelDefaults.detailsData
        })
    }

    @Action(SetFinOpsAzureResource)
    setFinOpsAzureResource(ctx: StateContext<FinOpsAzureResourcesStateModel>, {subscriptionId, resourceId, selectedDate}: SetFinOpsAzureResource) {
        // If no selected date, retrieve from config
        let selectedDateStr = ""
        if (selectedDate === undefined) {
            selectedDateStr = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        } else {
            selectedDateStr = this.dateService.formatShortDate(selectedDate)
        }
        return this.finOpsAzureResourcesService.loadFinOpsAzureResourceDetails(
            selectedDateStr,
            subscriptionId!,
            resourceId
        ).pipe(
            tap(value => {
                return ctx.patchState({
                    detailsData: value
                })
            }),
        )
    }

    @Action(LoadFinOpsAzureResourcesChartsData)
    loadFinOpsAzureResourcesChartsData(ctx: StateContext<FinOpsAzureResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList: Observable<FinOpsAzureResourcesDayMeasures>[] = []
        let subscriptions: string[] = []

        azureConfig.subscriptions.forEach(subscription => {
            subscriptions.push(subscription.subscription_id)
        });
        
        for(const _subscription of subscriptions) {
            for (const historicalDate of historicalDates.map(item => item.date)) {
                const FINOPS_RESOURCE_CHARTS_DATE_KEY = this.KEY
                + "-charts-"
                + "subscription-" + _subscription
                + "-date-" + historicalDate

                if(!this.metaService.isStale(FINOPS_RESOURCE_CHARTS_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_RESOURCE_CHARTS_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }

                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_RESOURCE_CHARTS_DATE_KEY,
                        this.finOpsAzureResourcesService.loadFinOpsAzureResourcesMeasures(historicalDate, _subscription),
                        (chart_resources) => {
                            ctx.setState(state => {
                                const shallowCloneAllChartsData = {...state.chartsDataBySubscription}
                                shallowCloneAllChartsData[_subscription] = {...(shallowCloneAllChartsData[_subscription] || {})}
                                shallowCloneAllChartsData[_subscription][historicalDate] = chart_resources.measures
                                return {
                                    ...state,
                                    chartsDataBySubscription: shallowCloneAllChartsData
                                }                                
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(
                    new LoadFinOpsAzureResourcesAllSubscriptionsChartsFilteredData()
                )
            })
        )
    }

    @Action(LoadFinOpsAzureResourcesAllSubscriptionsChartsFilteredData)
    loadFinOpsAzureResourcesAllSubscriptionsChartsFilteredData(ctx: StateContext<FinOpsAzureResourcesStateModel>){
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        
        // All chartsData for all subscriptions and for all retrieved dates.
        const bulkChartsData = ctx.getState().chartsDataBySubscription

        // Retrieve granularity 
        const granularity = chartsConfig.granularity

        // Retrieve filters to adjust costs depending on selected filters
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_RESOURCES_CHARTS]]

        // Get all date to retrieve data from
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)

        // Get date for compare Chart
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]


        const aggregatedHistoricalData = FinOpsAzureResourcesHelper.computeAggregatedHistoricalChartData(
            azureConfig,
            historicalDatesSelected,
            bulkChartsData,
            filters,
            granularity,
            this.floorPipe, this.translate, this.dateService
        )

        const aggregatedCompareTypesData = FinOpsAzureResourcesHelper.computeAggredatedCompareTypesChartData(
            azureConfig,
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe, this.numberFormatPipe, this.dateService, this.azureResourceTypeLabelPipe
        )

        const aggregatedCompareProjectsData = FinOpsAzureResourcesHelper.computeAggregatedCompareProjectsChartData(
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe,
            this.numberFormatPipe,
            this.dateService
            );

        ctx.patchState({
            chartsDataDisplayed: {
                filteredHistoricalChartData: aggregatedHistoricalData,
                filteredCompareTypesChartData: aggregatedCompareTypesData,
                filteredCompareProjectsChartData: aggregatedCompareProjectsData
            }
        })
    }

}
